import { InputBox } from '@/components/atoms/InputBox';
import { Button } from '@/components/atoms/NewPcButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/Select';
import { OrderSeachField } from '@/types/order-list';
import { AnimatePresence, motion } from 'framer-motion';
import { startTransition, useEffect, useState } from 'react';

interface SearchFilterProps {
  searchField: string;
  searchKeyword: string;
  setSearchKeyword: (keyword: string) => void;
  setSearchField: (field: OrderSeachField) => void;
  onSearch: (keyword: string) => void;
  onReset: () => void;
  /** filter: 보여줄 옵션 value 배열 (예: ['ProductName', 'ReceiverName']) */
  filter?: string[];
}

const searchOptions = [
  { value: 'ExternalProductInfo', label: '판매자상품코드' },
  { value: 'ProductName', label: '상품명' },
  { value: 'ReceiverName', label: '수령자' },
  { value: 'OrdererName', label: '구매자' },
];

export const SearchFilter = ({
  searchField,
  setSearchField,
  searchKeyword,
  setSearchKeyword,
  onSearch,
  onReset,
  filter,
}: SearchFilterProps) => {
  const [keyword, setKeyword] = useState('');

  const handleClick = () => {
    if (searchKeyword) {
      startTransition(() => {
        onReset();
        setKeyword('');
      });
    } else {
      startTransition(() => {
        onSearch(keyword);
      });
    }
  };

  // filter prop이 있으면 해당 value만 보여주고, 없으면 전체 옵션
  const filteredOptions = filter
    ? searchOptions.filter((option) => filter.includes(option.value))
    : searchOptions;

  useEffect(() => {
    if (!keyword) setSearchKeyword('');
  }, [keyword]);

  return (
    <div className="flex h-full items-center gap-2">
      <Select value={searchField} onValueChange={setSearchField}>
        <SelectTrigger className="h-[40px] w-[160px] border-gr-300 Body5M14">
          <SelectValue placeholder="검색조건" />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {filteredOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <InputBox
        leftIcon
        type="text"
        placeholder="검색어를 입력하세요"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && keyword) {
            onSearch(keyword);
          }
        }}
        className="h-[44px] w-[320px] border-gr-300 focus-visible:ring-point-500"
      />
      <Button
        size="xsmall"
        variant="secondary"
        onClick={handleClick}
        className="relative w-[45px] overflow-hidden px-3 transition-all duration-300"
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={searchKeyword ? 'cancel' : 'search'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.1 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            {searchKeyword ? (
              <div className="flex items-center gap-1">
                <span>취소</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <span>검색</span>
              </div>
            )}
          </motion.span>
        </AnimatePresence>
      </Button>
    </div>
  );
};
